import { useAppSelector } from '../../app/hooks';
import { useState, useEffect, useMemo } from 'react';
import { getFilteredViews, FilteredView } from './FilteredViewHelper';

interface FilteredViewOption {
    label: string;
    value: string;
    endLabel?: string;
    columns?: string[];
}

const useFetchFilteredViewData = (source: string) => {
    const [filteredViews, setFilteredViews] = useState<FilteredViewOption[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const filterMapMemo = useMemo(() => {
        return new Map<string, any>();
    }, []);

    const fetchFilterKeywords = () => {
        switch (source) {
            case 'LEADS':
                return 'LEADS_PAGE';
            case 'DEALS':
                return 'DEALS';
            case 'DEALSVIEW':
                return 'DEALS';
            case 'MANDATE_VIEW':
            case 'DEAL_VIEW':
                return 'DEALS'; // Use same key for both views
            default:
                return 'LEADS_PAGE';
        }
    };

    const appliedFiltersFromState = useAppSelector((state) => state.investor.customFilterDrawer?.appliedFilters?.[fetchFilterKeywords()]);
    const organizationId = useAppSelector((state) => state.user.userData?.investorOrgId);

    const fetchViews = async () => {
        if (!organizationId) return;

        setIsLoading(true);
        setError(null);

        try {
            const views = await getFilteredViews({
                pnoId: organizationId,
                source,
            });

            // views is now always an array (never false) due to updated error handling
            if (Array.isArray(views)) {
                const options = views.map((view: FilteredView) => ({
                    label: view.viewName,
                    value: view.viewId,
                    endLabel: view.userName,
                    columns: view.columns,
                }));
                setFilteredViews(options);

                // create a map of viewId to filters and columns
                const filtersMap = views.reduce((acc: Record<string, any>, view: FilteredView) => {
                    acc[view.viewId] = {
                        filters: view.filters,
                        columns: view.columns,
                    };
                    return acc;
                }, {});
                filterMapMemo.clear();
                Object.entries(filtersMap).forEach(([key, value]) => {
                    filterMapMemo.set(key, value);
                });
            } else {
                // This shouldn't happen with updated error handling, but keep as fallback
                setFilteredViews([]);
                filterMapMemo.clear();
            }
        } catch (err) {
            console.error('Error in fetching filtered views:', err);
            setError('Error fetching filtered views');
            setFilteredViews([]); // Set empty array on error
            filterMapMemo.clear(); // Clear map on error
        } finally {
            setIsLoading(false);
        }
    };
    useEffect(() => {
        fetchViews();
    }, [source]);

    const refetch = () => {
        fetchViews();
    };

    return {
        appliedFiltersFromState,
        filteredViews,
        isLoading,
        error,
        filterMapMemo,
        refetch,
    };
};

export default useFetchFilteredViewData;
